import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
    InformationCircleIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';

interface TooltipProps {
    content: string | string[];
    children: React.ReactNode;
    type?: 'default' | 'info' | 'error' | 'warning' | 'success';
    className?: string;
    delay?: number;
}

const Tooltip: React.FC<TooltipProps> = ({
                                             content,
                                             children,
                                             type = 'default',
                                             className = '',
                                             delay = 300
                                         }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const tooltipRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = (e: React.MouseEvent) => {
        updateMousePosition(e);
        timeoutRef.current = setTimeout(() => {
            setIsVisible(true);
        }, delay);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        updateMousePosition(e);
    };

    const handleMouseLeave = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsVisible(false);
    };

    const updateMousePosition = (e: React.MouseEvent) => {
        setMousePosition({
            x: e.clientX,
            y: e.clientY,
        });
    };

    const getTooltipStyling = () => {
        switch (type) {
            case 'info':
                return {
                    container: 'bg-blue-50 border border-blue-200 text-blue-800',
                    icon: <InformationCircleIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />,
                };
            case 'error':
                return {
                    container: 'bg-red-50 border border-red-200 text-red-800',
                    icon: <ExclamationCircleIcon className="h-4 w-4 text-red-600 flex-shrink-0" />,
                };
            case 'warning':
                return {
                    container: 'bg-yellow-50 border border-yellow-200 text-yellow-800',
                    icon: <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600 flex-shrink-0" />,
                };
            case 'success':
                return {
                    container: 'bg-green-50 border border-green-200 text-green-800',
                    icon: <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />,
                };
            default:
                return {
                    container: 'bg-gray-900 text-white',
                    icon: null,
                };
        }
    };

    const renderTooltipContent = () => {
        const styling = getTooltipStyling();
        const messages = Array.isArray(content) ? content : [content];

        if (type === 'default') {
            return (
                <div
                    className={`px-3 py-2 text-sm rounded-lg shadow-lg pointer-events-none max-w-xs ${styling.container}`}
                >
                    {content}
                </div>
            );
        }

        return (
            <div className={`p-3 rounded-lg shadow-lg pointer-events-none max-w-sm ${styling.container}`}>
                <div className="flex items-start space-x-2">
                    {styling.icon}
                    <div className="flex-1 min-w-0">
                        {messages.length === 1 ? (
                            <p className="text-sm font-medium">{messages[0]}</p>
                        ) : (
                            <div>
                                <p className="text-sm font-medium mb-2">
                                    {type === 'error'
                                        ? 'Issues found:'
                                        : type === 'warning'
                                            ? 'Warnings:'
                                            : type === 'success'
                                                ? 'Success:'
                                                : 'Information:'}
                                </p>
                                <ul className="text-sm space-y-1 list-disc list-inside">
                                    {messages.map((message, index) => (
                                        <li key={index}>{message}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const getTooltipStyle = () => {
        const offset = 10; // Distance from cursor
        let x = mousePosition.x + offset;
        let y = mousePosition.y - offset;

        if (tooltipRef.current) {
            const rect = tooltipRef.current.getBoundingClientRect();

            if (x + rect.width > window.innerWidth - 10) {
                x = mousePosition.x - rect.width - offset;
            }

            if (y + rect.height > window.innerHeight - 10) {
                y = mousePosition.y - rect.height - offset;
            }

            if (y < 10) {
                y = mousePosition.y + offset + 20;
            }
        }

        return {
            left: `${x}px`,
            top: `${y}px`,
        };
    };

    return (
        <>
            <div
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                className={className}
            >
                {children}
            </div>
            {isVisible &&
                ReactDOM.createPortal(
                    <div ref={tooltipRef} className="fixed z-[9999]" style={getTooltipStyle()}>
                        {renderTooltipContent()}
                    </div>,
                    document.body
                )}
        </>
    );
};

export default Tooltip;
